import { useState, useCallback } from 'react';
import { Tradesperson } from '@/lib/types';
import { getPostcodeTradespeople, getPopularTradespeople } from '@/lib/supabase/trades';
import { debug } from '@/lib/utils';
import { toast } from "sonner";

/**
 * Hook for searching tradespeople
 * @param userId The current user's ID
 * @returns Methods and state for searching tradespeople
 */
export function useTradesSearch(userId: string) {
  const [tradespeople, setTradespeople] = useState<Tradesperson[]>([]);
  const [isSearching, setIsSearching] = useState(false);

  /**
   * Search for tradespeople by query text
   * @param query The search query
   */
  const searchTradespeople = useCallback(async (query: string) => {
    if (!query?.trim()) {
      setTradespeople([]);
      return;
    }

    setIsSearching(true);
    
    try {
      // First search by postcode if the query looks like a postcode
      const postcodeRegex = /^[A-Z]{1,2}[0-9]{1,2}$/i;
      if (postcodeRegex.test(query.trim())) {
        const postcodeResults = await getPostcodeTradespeople(query.trim());
        
        if (postcodeResults.length > 0) {
          // Sort by recommendation count
          const sorted = [...postcodeResults].sort((a, b) => 
            (b.recommendation_count || 0) - (a.recommendation_count || 0)
          );
          
          setTradespeople(sorted);
          setIsSearching(false);
          return;
        }
      }
      
      // If no postcode results, fall back to popular tradespeople
      const popularResults = await getPopularTradespeople();
      setTradespeople(popularResults);
      
    } catch (error) {
      debug.error('Error searching tradespeople:', error);
      toast.error('Failed to search for tradespeople');
      setTradespeople([]);
    } finally {
      setIsSearching(false);
    }
  }, []);

  return {
    tradespeople,
    searchTradespeople,
    isSearching
  };
}